/** Varijante asseta jedne verzije modela (PLAN §4). */
export const VARIANTS = ['mobile', 'desktop', 'ar', 'poster', 'poster2x', 'usdz'] as const;
export type Variant = (typeof VARIANTS)[number];

/** 3D varijante koje embed bira za prikaz (bez postera i USDZ-a). */
export const MODEL_VARIANTS = ['mobile', 'desktop', 'ar'] as const;
export type ModelVariant = (typeof MODEL_VARIANTS)[number];

export interface VariantBudget {
  maxTriangles: number;
  maxTexturePx: number;
  targetBytes: number;
}

const MB = 1024 * 1024;

/** Budžeti varijanti (PLAN §8, korak 5). */
export const VARIANT_BUDGETS: Readonly<Record<ModelVariant, VariantBudget>> = {
  mobile: { maxTriangles: 50_000, maxTexturePx: 1024, targetBytes: 2 * MB },
  desktop: { maxTriangles: 150_000, maxTexturePx: 2048, targetBytes: 6 * MB },
  ar: { maxTriangles: 100_000, maxTexturePx: 2048, targetBytes: 4 * MB },
};

/**
 * Izvor za USDZ (Quick Look): bez kompresije kao `ar`, ali budžet telefona. USDZ geometriju
 * piše kao tekst, pa bi iz `ar` (100k) izašlo i 11 MB.
 */
export const USDZ_SOURCE_BUDGET: VariantBudget = {
  maxTriangles: 50_000,
  maxTexturePx: 1024,
  targetBytes: 3 * MB,
};

/** Posteri: stranica kvadrata u pikselima. */
export const POSTER_SIZES_PX = { poster: 800, poster2x: 1600 } as const;

/**
 * Istek asset tokena se zaokružuje na kraj sata, da URL bude stabilan i da ga
 * browser kešira između prikaza (PLAN §6.2).
 */
export const TOKEN_BUCKET_SEC = 3600;
/** Token uvek važi bar ovoliko; inače istek ide na kraj sledećeg sata. */
export const TOKEN_MIN_TTL_SEC = 600;

/** Koliko klijent sme da drži `/v1/resolve` odgovor. */
export const RESOLVE_CLIENT_CACHE_SEC = 60;

/** Ispod ove širine embed bira `mobile` varijantu. */
export const MOBILE_BREAKPOINT_PX = 768;

export const TENANT_STATUSES = ['active', 'suspended'] as const;
export const API_KEY_KINDS = ['publishable', 'secret'] as const;
export const MODEL_STATUSES = ['draft', 'processing', 'ready', 'archived'] as const;
export const LICENSE_SCOPES = ['model', 'brand', 'category', 'all'] as const;
export const EVENT_TYPES = ['view', 'interact', 'ar_open', 'error', 'badge_click'] as const;
/** Gde model stoji u AR-u: na podu/pultu ili na zidu (mešači, baterije, tuš sistemi). */
export const AR_PLACEMENTS = ['floor', 'wall'] as const;
export type ArPlacement = (typeof AR_PLACEMENTS)[number];

export const CONTENT_TYPES: Readonly<Record<Variant, string>> = {
  mobile: 'model/gltf-binary',
  desktop: 'model/gltf-binary',
  ar: 'model/gltf-binary',
  poster: 'image/webp',
  poster2x: 'image/webp',
  usdz: 'model/vnd.usdz+zip',
};

/** R2 ključevi (PLAN §4). */
export const r2Keys = {
  master: (modelId: string, version: number): string => `master/${modelId}/${version}/source.glb`,
  variant: (modelId: string, version: number, variant: Variant): string => {
    const file: Record<Variant, string> = {
      mobile: 'mobile.glb',
      desktop: 'desktop.glb',
      ar: 'ar.glb',
      poster: 'poster.webp',
      poster2x: 'poster@2x.webp',
      usdz: 'ar.usdz',
    };
    return `m/${modelId}/${version}/${file[variant]}`;
  },
};

/**
 * Ugao kamere (model-viewer camera-orbit) za poster I za prikaz u embedu — isti ugao znači da
 * zamena poster → model ne „skače".
 */
export const DEFAULT_CAMERA_ORBIT = '-35deg 70deg auto';

/** Magic link za portal važi 15 minuta i jednokratan je. */
export const MAGIC_LINK_TTL_SEC = 15 * 60;
/** Sesija portala (HttpOnly kolačić na dashboard-u). */
export const SESSION_TTL_SEC = 30 * 24 * 3600;
/** Naziv kolačića sesije na dashboard-u. */
export const SESSION_COOKIE = 'km3d_session';

/** Pinovana verzija model-viewer-a (embed i dashboard pregled); embed postbuild proverava da se slaže. */
export const MODEL_VIEWER_VERSION = '4.3.1';
export const MODEL_VIEWER_PATH = `/embed/mv/model-viewer-${MODEL_VIEWER_VERSION}.min.js`;

/** Poslovi pipeline-a (upload iz dashboard-a → worker na VPS-u). */
export const JOB_STATUSES = ['queued', 'running', 'done', 'failed'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];
/** Posao bez heartbeat-a duže od ovoga se smatra napuštenim i vraća u red. */
export const JOB_STALE_SEC = 10 * 60;
/** Posle ovoliko pokušaja posao ostaje `failed`. */
export const JOB_MAX_ATTEMPTS = 3;
/** Najveći GLB koji se prima kroz dashboard (Workers telo zahteva je do 100 MB). */
export const UPLOAD_MAX_BYTES = 95 * 1024 * 1024;

/** Varijante koje dobijaju fingerprint kopiju (posteri i USDZ ostaju zajednički). */
export const FINGERPRINT_VARIANTS = ['mobile', 'desktop', 'ar'] as const;

/** R2 ključ fingerprint kopije. */
export const fingerprintKey = (
  modelId: string,
  version: number,
  tenantId: string,
  variant: string,
): string => `m/${modelId}/${version}/t/${tenantId}/${variant}.glb`;
